import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchCfoComplianceScore,
  fetchCfoCoverageHeatmap,
  fetchCfoOpenIssues,
  fetchCfoProgramProgress,
  fetchCfoRepeatEscalated,
  fetchCfoRiskLandscape,
  fetchCfoSOCCoverage,
  fetchCfoScoreTrend,
  type CFOComplianceScoreResponse,
  type CFOCoverageHeatmapRow,
  type CFOOpenIssuesResponse,
  type CFOProgramProgressRow,
  type CFORepeatEscalatedRow,
  type CFORiskLandscapeRow,
  type CFOSOCCoverageRow,
  type CFOScoreTrendRow,
} from '../../request/analytics/cfoReports.ts';
import { analyticsCfoQueryKeys } from '../../queries/analytics/keys.ts';
import { useAnalyticsScope } from './useAnalyticsScope.tsx';
import { scopeKey } from '../../request/analytics/scope.ts';

/**
 * React Query v5 hooks for the CFO/Executive-persona analytics dashboard
 * (Feature 5.3, reports #1-#8). Each report is its own independent query
 * so a single failing/slow widget doesn't block the rest of the dashboard.
 */

export function useCfoComplianceScore(): UseQueryResult<CFOComplianceScoreResponse | null, Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsCfoQueryKeys.complianceScore(scopeKey(scope)),
    queryFn: () => fetchCfoComplianceScore(scope),
  });
}

export function useCfoCoverageHeatmap(): UseQueryResult<CFOCoverageHeatmapRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsCfoQueryKeys.coverageHeatmap(scopeKey(scope)),
    queryFn: () => fetchCfoCoverageHeatmap(scope),
  });
}

export function useCfoOpenIssues(): UseQueryResult<CFOOpenIssuesResponse, Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsCfoQueryKeys.openIssues(scopeKey(scope)),
    queryFn: () => fetchCfoOpenIssues(scope),
  });
}

export function useCfoProgramProgress(): UseQueryResult<CFOProgramProgressRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsCfoQueryKeys.programProgress(scopeKey(scope)),
    queryFn: () => fetchCfoProgramProgress(scope),
  });
}

export function useCfoSOCCoverage(): UseQueryResult<CFOSOCCoverageRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsCfoQueryKeys.socCoverage(scopeKey(scope)),
    queryFn: () => fetchCfoSOCCoverage(scope),
  });
}

export function useCfoRiskLandscape(): UseQueryResult<CFORiskLandscapeRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsCfoQueryKeys.riskLandscape(scopeKey(scope)),
    queryFn: () => fetchCfoRiskLandscape(scope),
  });
}

export function useCfoRepeatEscalated(): UseQueryResult<CFORepeatEscalatedRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsCfoQueryKeys.repeatEscalated(scopeKey(scope)),
    queryFn: () => fetchCfoRepeatEscalated(scope),
  });
}

export function useCfoScoreTrend(): UseQueryResult<CFOScoreTrendRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsCfoQueryKeys.scoreTrend(scopeKey(scope)),
    queryFn: () => fetchCfoScoreTrend(scope),
  });
}
